// ===========================================================================
// WebRTC peer connection manager.
//
// Media (audio/video) flows peer-to-peer over a forced TURN relay, so neither
// peer learns the other's IP (iceTransportPolicy:"relay" comes from the server's
// welcome). A DataChannel carries peer-to-peer FILE transfers (images, video,
// audio) so large files never touch our server. Text chat does NOT use the
// DataChannel -- it is relayed through the server so the server can count
// messages for the skip-unlock rule. ASCII only.
// ===========================================================================

import { isShareableFile } from '@lumi/shared';

const FILE_CHUNK = 16 * 1024;

export class PeerConnection {
  /**
   * @param {object} opts
   * @param {RTCIceServer[]} opts.iceServers
   * @param {'relay'|'all'} opts.iceTransportPolicy
   * @param {boolean} opts.isCaller
   * @param {MediaStream|null} opts.localStream
   * @param {object} cb  { onRemoteStream, onIce, onFile, onState }
   */
  constructor(opts, cb) {
    this.cb = cb;
    this.isCaller = opts.isCaller;
    this.incoming = new Map(); // file id -> { meta, chunks, received }

    // RELAY ONLY, decided here rather than accepted from the server.
    //
    // "The person you meet never sees your IP address" is the site's headline
    // promise, and it rested entirely on the server sending
    // iceTransportPolicy: 'relay' in WELCOME. Anything else -- a config
    // mistake, an env var lost in a restart, a caller that passes no ice
    // config at all (verify.js and support.js both did, yielding {} and the
    // browser default 'all') -- and the browser gathers host and srflx
    // candidates and posts them straight to the stranger, with nothing on this
    // side to notice. A promise that can be broken by a missing field is not a
    // promise.
    //
    // So the default is 'relay' and only an explicit, deliberate 'all' turns
    // it off. The SFU path cannot leak an address at all -- media terminates
    // at mediasoup and peers never exchange candidates -- so this is the guard
    // for the legacy peer-to-peer path, which is the one that can.
    const policy = opts.iceTransportPolicy === 'all' ? 'all' : 'relay';
    this.relayOnly = policy === 'relay';
    this.pc = new RTCPeerConnection({
      iceServers: opts.iceServers,
      iceTransportPolicy: policy,
      bundlePolicy: 'max-bundle',
    });

    if (opts.localStream) {
      for (const track of opts.localStream.getTracks()) {
        this.pc.addTrack(track, opts.localStream);
      }
    }

    this.remoteStream = new MediaStream();
    this.pc.ontrack = (ev) => {
      for (const track of ev.streams[0] ? ev.streams[0].getTracks() : [ev.track]) {
        this.remoteStream.addTrack(track);
      }
      cb.onRemoteStream?.(this.remoteStream);
    };

    this.pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      // Belt and braces: even under 'relay' a browser bug, or a policy this
      // constructor did not set, must not hand the peer a host address. A
      // relay candidate names the TURN server, which is ours and public; the
      // others name the person.
      if (this.relayOnly && ev.candidate.type && ev.candidate.type !== 'relay') return;
      cb.onIce?.(ev.candidate.toJSON());
    };
    this.pc.onconnectionstatechange = () => cb.onState?.(this.pc.connectionState);

    if (this.isCaller) {
      this.channel = this.pc.createDataChannel('lumi-data', { ordered: true });
      this._wireChannel(this.channel);
    } else {
      this.pc.ondatachannel = (ev) => {
        this.channel = ev.channel;
        this._wireChannel(this.channel);
      };
    }
  }

  _wireChannel(ch) {
    ch.binaryType = 'arraybuffer';
    ch.onmessage = (ev) => this._onChannelMessage(ev.data);
  }

  async createOffer() {
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return this.pc.localDescription;
  }

  async acceptOffer(offer) {
    await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return this.pc.localDescription;
  }

  async acceptAnswer(answer) {
    await this.pc.setRemoteDescription(new RTCSessionDescription(answer));
  }

  async addIce(candidate) {
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {
      /* late or duplicate candidate -- safe to ignore */
    }
  }

  // ---- File transfer over the DataChannel --------------------------------

  /** Send a File/Blob (image, video, or audio only). Returns false if rejected. */
  async sendFile(file) {
    if (!this.channel || this.channel.readyState !== 'open') return false;
    if (!isShareableFile(file.type, file.size)) return false;
    const id = Math.random().toString(36).slice(2);
    this.channel.send(
      JSON.stringify({ k: 'file-meta', id, name: file.name, mime: file.type, size: file.size }),
    );
    const buf = await file.arrayBuffer();
    for (let off = 0; off < buf.byteLength; off += FILE_CHUNK) {
      // Apply backpressure so we do not overflow the channel buffer.
      while (this.channel.bufferedAmount > 4 * 1024 * 1024) {
         
        await new Promise((r) => setTimeout(r, 20));
      }
      this.channel.send(buf.slice(off, off + FILE_CHUNK));
    }
    this.channel.send(JSON.stringify({ k: 'file-end', id }));
    return true;
  }

  _onChannelMessage(data) {
    if (typeof data === 'string') {
      let m;
      try {
        m = JSON.parse(data);
      } catch {
        return;
      }
      if (m.k === 'file-meta') {
        // Validate before accepting any bytes.
        if (!isShareableFile(m.mime, m.size)) return;
        this.incoming.set(m.id, { meta: m, chunks: [], received: 0 });
        this._pendingId = m.id;
      } else if (m.k === 'file-end') {
        const rec = this.incoming.get(m.id);
        this.incoming.delete(m.id);
        this._pendingId = null;
        if (!rec) return;
        const blob = new Blob(rec.chunks, { type: rec.meta.mime });
        if (!isShareableFile(blob.type, blob.size)) return;
        this.cb.onFile?.({ name: rec.meta.name, mime: rec.meta.mime, blob });
      }
      return;
    }
    // Binary chunk -- belongs to the most recently announced file.
    const rec = this._pendingId && this.incoming.get(this._pendingId);
    if (!rec) return;
    rec.received += data.byteLength;
    if (rec.received > rec.meta.size + FILE_CHUNK) {
      // Sender lied about size; drop.
      this.incoming.delete(this._pendingId);
      this._pendingId = null;
      return;
    }
    rec.chunks.push(data);
  }

  close() {
    try {
      this.channel?.close();
    } catch {
      /* ignore */
    }
    try {
      this.pc.getSenders().forEach((s) => s.track && s.track.stop && undefined);
      this.pc.close();
    } catch {
      /* ignore */
    }
  }
}
